import {
  FC,
  Dispatch,
  SetStateAction,
  ReactElement,
  Children,
  useMemo,
  useRef,
  useCallback,
  useState,
} from 'react';
import {
  AdaptedEntity,
  Column,
  GenericEntityType,
  GenericProviderType,
  Pagination,
  ProviderFilters,
  TableCellProps,
  TemplateEntity,
  TextFieldValue,
  useLoaderNunjucks,
  WbResponsiveTableToolbar,
  wbSafeNunjucksApiRef,
  WbTable,
} from '@agilelab/plugin-wb-platform';
import {
  Box,
  createStyles,
  FormControl,
  makeStyles,
  Typography,
} from '@material-ui/core';
import { cloneElement, isValidElement } from 'react';
import { isEqual } from 'lodash';
import {
  getLabelAndValue,
  stableStringify,
  stripInternalEntityProps,
} from './utils';
import { AiToggle } from './MultiSelection/AiToggle';
import { useGeneralPurposePickerContext } from '../GeneralPurposePickerContext';
import { useApi } from '@mainstage/core-plugin-api';
import { WbAlert } from '../../WbAlert';
import { CustomError } from '@agilelab/plugin-wb-platform-common';

/**
 * Converts an error thrown while fetching entities (e.g. a KGM `CustomError`
 * with a generic `message` and a detailed `errorMessage`) into a WbAlert
 * message. Falls back to the plain `Error.message` for non-CustomError values.
 */
function errorToPreviewMessage(error: Error) {
  const customError =
    error instanceof CustomError ? (error as CustomError) : undefined;
  return {
    type: 'error' as const,
    title: customError?.message ?? error.message ?? 'Failed to load entities',
    content: customError?.errorMessage ?? '',
  };
}

const renderRow = (
  parsedNunjuck: string,
  isAiBestMatchLabel: boolean,
  classes: ReturnType<typeof useStyles>,
) => {
  if (isAiBestMatchLabel) {
    return (
      <Typography>
        <span className={classes.boxBest}>{parsedNunjuck}</span>
      </Typography>
    );
  }
  return <Typography>{parsedNunjuck}</Typography>;
};

interface SelectableEntityTableProps {
  loading: boolean;
  data: GenericEntityType[];
  /** Error from the provider's entities fetch, if the last request failed. */
  error?: Error;
  entities: TextFieldValue[];
  adapter: (entity: GenericEntityType) => AdaptedEntity;
  onChangeHandler: (entities: TextFieldValue[]) => void;
  filtersSection: () => JSX.Element;
  count: number;
  pagination: Pagination;
  columns: Column[];
  setPagination: Dispatch<SetStateAction<Pagination>>;
  multiple?: boolean;
  prevCursor?: string;
  nextCursor?: string;
  templateEntity: TemplateEntity;
  isAiMatchAvailable?: boolean;
  getAllFilteredEntities?: () => Promise<GenericEntityType[]>;
  treeMode?: GenericProviderType<
    GenericEntityType[],
    ProviderFilters
  >['treeMode'];
}

const useStyles = makeStyles(
  theme =>
    createStyles({
      formControl: {
        width: '100%',
        overflow: 'auto',
        height: '100%',
        justifyContent: 'space-between',
      },
      boxBest: {
        padding: `${theme.spacing(0.5)}px ${theme.spacing(1)}px`,
        backgroundColor: theme.palette.white,
        border: 'solid 1px transparent',
        borderRadius: '4px',
        background:
          'linear-gradient( #ffffff, #ffffff) padding-box,linear-gradient(90deg, #EE72D3, #7C4DFF) border-box',
        whiteSpace: 'nowrap',
      },
      aiSuggestedRow: {
        backgroundColor: `${theme.palette.bkg.accent} !important`,
        '& td': {
          borderBottom: `1px solid ${theme.palette.accent.light}`,
        },
      },
      aiFilterButtonClass: {
        width: 'auto',
      },
    }),
  { name: 'selectionTable' },
);

export const SelectableEntityTable: FC<SelectableEntityTableProps> = ({
  loading,
  data,
  error,
  entities,
  adapter,
  onChangeHandler,
  filtersSection,
  multiple = false,
  pagination,
  setPagination,
  count,
  prevCursor,
  nextCursor,
  columns,
  templateEntity,
  isAiMatchAvailable = false,
  getAllFilteredEntities,
  treeMode,
}) => {
  const classes = useStyles();
  const {
    isAiGenerated,
    fetchAiBestMatch,
    setAiState,
    setSuggestedItems,
    maxItems,
    checkOptionDisabled,
  } = useGeneralPurposePickerContext();
  const nunjucksApi = useApi(wbSafeNunjucksApiRef);

  // Preload nunjucks templates used in columns
  const nunjucksPaths = useMemo(() => columns.map(c => c.path), [columns]);
  const { isReady } = useLoaderNunjucks(nunjucksPaths);

  // Reference to keep track of AI added entities
  const aiAddedRef = useRef<TextFieldValue[]>([]);

  const [expandedRowsIds, setExpandedRowsIds] = useState<string[]>([]);

  const getRowId = useCallback(
    (row: GenericEntityType): string => {
      const adapted = adapter(row);
      const rowValue = getLabelAndValue(row, adapted, templateEntity).value;
      if (typeof rowValue === 'string') return rowValue;
      // Strip transient tree/search-only properties and use a stable
      // (key-order independent) stringification so the same concept yields
      // the same identity regardless of how the row was fetched (tree
      // expansion vs. search).
      return stableStringify(
        stripInternalEntityProps(rowValue as Record<string, unknown>),
      );
    },
    [adapter, templateEntity],
  );

  // In tree mode, use __treeId for row identity (depthMap, expand, React keys).
  // This is required because the same IRI can appear under multiple parents.
  const effectiveGetRowReactKey = useCallback(
    (row: GenericEntityType): string => {
      if (treeMode) return treeMode.getEntityId(row);
      return getRowId(row);
    },
    [treeMode, getRowId],
  );

  const treePropsConfig = useMemo(() => {
    if (!treeMode) return undefined;
    return {
      getRowChildren: (row: GenericEntityType) => treeMode.getRowChildren(row),
      expandedRowsIds,
      onExpandChange: setExpandedRowsIds,
      onExpand: treeMode.onExpand,
      loadingRowsIds: treeMode.loadingRowsIds,
      getLoadMoreParentId: (row: GenericEntityType) =>
        typeof row.__loadMoreParentId === 'string'
          ? row.__loadMoreParentId
          : undefined,
      getLoadingParentId: (row: GenericEntityType) =>
        typeof row.__loadingParentId === 'string'
          ? row.__loadingParentId
          : undefined,
      onLoadMoreChildren: treeMode.onLoadMoreChildren,
      hasChildren: treeMode.hasChildren,
    };
  }, [treeMode, expandedRowsIds]);

  const deriveEntityId = useCallback((entity: TextFieldValue): string => {
    const { value } = entity;
    if (typeof value === 'string') return value;
    return stableStringify(
      stripInternalEntityProps(value as Record<string, unknown>),
    );
  }, []);

  // Build a set of IRI-based selection IDs for matching.
  const entityIdsSet = useMemo(() => {
    return new Set(entities.map(entity => deriveEntityId(entity)));
  }, [entities, deriveEntityId]);

  // Collect all visible rows including tree children for selection comparison.
  // Without this, checkboxes on expanded tree children would never appear checked
  // because `data` only contains root-level rows.
  const allVisibleRows = useMemo(() => {
    if (!treeMode) return data;
    const collect = (rows: GenericEntityType[]): GenericEntityType[] => {
      const result: GenericEntityType[] = [];
      for (const row of rows) {
        result.push(row);
        const children = treeMode.getRowChildren(row);
        if (children.length > 0) {
          result.push(...collect(children));
        }
      }
      return result;
    };
    return collect(data);
  }, [data, treeMode]);

  const selectedItems = useMemo(() => {
    return allVisibleRows.filter(row => {
      const rowId = getRowId(row);
      return entityIdsSet.has(rowId);
    });
  }, [allVisibleRows, entityIdsSet, getRowId]);

  // Keep a ref to selectedItems so setSelectedItems can compare prev vs new
  // without needing selectedItems in its dependency array (avoids stale closures).
  const selectedItemsRef = useRef(selectedItems);
  selectedItemsRef.current = selectedItems;

  const setSelectedItems = useCallback(
    (newSelectedItems: GenericEntityType[]) => {
      // When the same concept (IRI) appears under multiple tree parents,
      // the Selector only removes one copy (by __treeId) on uncheck.
      // Detect which IRIs lost copies and remove them entirely so that
      // unchecking any copy deselects the concept.
      const prevIriCounts = new Map<string, number>();
      for (const row of selectedItemsRef.current) {
        const iri = getRowId(row);
        prevIriCounts.set(iri, (prevIriCounts.get(iri) ?? 0) + 1);
      }
      const newIriCounts = new Map<string, number>();
      for (const row of newSelectedItems) {
        const iri = getRowId(row);
        newIriCounts.set(iri, (newIriCounts.get(iri) ?? 0) + 1);
      }
      // IRIs whose count decreased were unchecked — remove them entirely
      const uncheckedIris = new Set<string>();
      for (const [iri, prevCount] of prevIriCounts) {
        const newCount = newIriCounts.get(iri) ?? 0;
        if (newCount < prevCount) uncheckedIris.add(iri);
      }
      // Filter out all copies of unchecked IRIs
      const effectiveSelected = newSelectedItems.filter(
        row => !uncheckedIris.has(getRowId(row)),
      );

      // Deduplicate by IRI
      const seen = new Set<string>();
      const deduped = effectiveSelected.filter(row => {
        const iri = getRowId(row);
        if (seen.has(iri)) return false;
        seen.add(iri);
        return true;
      });

      // convert new selected items to TextFieldValue[]
      const newEntitiesFromCurrentPage = deduped.map(row => {
        const adaptedRow = getLabelAndValue(row, adapter(row), templateEntity);
        if (templateEntity.displayName) {
          adaptedRow.kindDisplayName = templateEntity.displayName;
        }
        return adaptedRow;
      });

      // In single mode the selection is always replaced by the new item.
      // In multiple mode, selections from other pages are preserved.
      const currentPageIds = new Set(allVisibleRows.map(row => getRowId(row)));
      const entitiesFromOtherPages = multiple
        ? entities.filter(entity => !currentPageIds.has(deriveEntityId(entity)))
        : [];

      const allEntities = [
        ...entitiesFromOtherPages,
        ...newEntitiesFromCurrentPage,
      ];

      const clippedEntities =
        maxItems !== undefined ? allEntities.slice(0, maxItems) : allEntities;

      onChangeHandler(clippedEntities);
    },
    [
      multiple,
      entities,
      allVisibleRows,
      getRowId,
      deriveEntityId,
      adapter,
      templateEntity,
      onChangeHandler,
      maxItems,
    ],
  );

  const addAiMatches = useCallback(
    async (options: GenericEntityType[]) => {
      const result = await fetchAiBestMatch(options);
      const aiBestMatch =
        result?.map(row =>
          getLabelAndValue(row, adapter(row), templateEntity, true),
        ) ?? [];

      // Add AI best matches that are not already in the selected entities
      const toAdd = aiBestMatch.filter(
        ai => !entities.some(e => isEqual(e.value, ai.value)),
      );
      aiAddedRef.current = toAdd;
      return [...entities, ...toAdd];
    },
    [fetchAiBestMatch, adapter, templateEntity, entities],
  );

  const removeAiMatches = useCallback(() => {
    const newSelection = entities.filter(
      e => !aiAddedRef.current.some(ai => isEqual(e, ai)),
    );
    aiAddedRef.current = [];
    return newSelection;
  }, [entities]);

  const handleChange = useCallback(
    async (checked?: boolean) => {
      setAiState(checked ?? false);
      setSuggestedItems([]);

      if (checked) {
        // fetch all available entities not paginated
        const options = (await getAllFilteredEntities?.()) ?? [];
        const newEntities = await addAiMatches(options);
        onChangeHandler(newEntities);
      } else {
        const newEntities = removeAiMatches();
        onChangeHandler(newEntities);
      }
    },
    [
      setAiState,
      setSuggestedItems,
      getAllFilteredEntities,
      addAiMatches,
      removeAiMatches,
      onChangeHandler,
    ],
  );

  const handleChangePage = useCallback(
    (newPage: number) => {
      setPagination(v => ({
        limit: v.limit ?? 0,
        currentPage: newPage,
        cursor: pagination.currentPage > newPage ? prevCursor : nextCursor,
      }));
    },
    [setPagination, pagination.currentPage, prevCursor, nextCursor],
  );

  const handleChangeRowsPerPage = useCallback(
    (limit: number) => {
      setPagination({
        limit: limit,
        currentPage: 0,
        cursor: undefined,
      });
    },
    [setPagination],
  );

  const tableColumns = useMemo((): TableCellProps<GenericEntityType>[] => {
    return columns.map((column, idx) => ({
      field: column.name,
      fieldRender: (row: GenericEntityType) => {
        if (column.renderCell) {
          return column.renderCell(row);
        }

        // Render nunjucks template with row data, before that we load the template in the useLoaderNunjucks hook
        const parsedNunjucks = isReady
          ? nunjucksApi.render(column.path, row)
          : '-';

        const isAiBestMatch = isAiGenerated(row);
        // for the first column, if the row is AI generated, we add the "Best match" badge
        if (idx === 0 && isAiBestMatch) {
          return (
            <Box
              key={column.name}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography>{parsedNunjucks || '-'}</Typography>
              <Typography className={classes.boxBest} variant="caption">
                Best match
              </Typography>
            </Box>
          );
        }
        return renderRow(
          parsedNunjucks ?? '-',
          column.path === 'aiBestMatchLabel',
          classes,
        );
      },
      headerName: column.name,
    }));
  }, [classes, columns, isAiGenerated, nunjucksApi, isReady]);

  const sortedData = useMemo(() => {
    if (!isAiMatchAvailable) return data;

    const aiRows: GenericEntityType[] = [];
    const normalRows: GenericEntityType[] = [];

    data.forEach(row => {
      if (isAiGenerated(row)) {
        aiRows.push(row);
      } else {
        normalRows.push(row);
      }
    });

    return [...aiRows, ...normalRows];
  }, [data, isAiMatchAvailable, isAiGenerated]);

  const aiToggleComponent = useMemo(
    () => (
      <AiToggle
        handleChange={handleChange}
        aiFilterButtonClass={classes.aiFilterButtonClass}
      />
    ),
    [handleChange, classes.aiFilterButtonClass],
  );

  const section = filtersSection();

  const filtersSectionWithAI = useMemo(() => {
    if (!isAiMatchAvailable) {
      return section;
    }

    const aiToggleWrapper = (
      <WbResponsiveTableToolbar.Actions key="ai-toggle">
        {aiToggleComponent}
      </WbResponsiveTableToolbar.Actions>
    );

    if (isValidElement(section) && !!(section as ReactElement).props.children) {
      return cloneElement(section, {}, [
        ...Children.toArray((section as ReactElement).props.children),
        aiToggleWrapper,
      ]);
    }

    return cloneElement(section, {}, [aiToggleWrapper]);
  }, [isAiMatchAvailable, section, aiToggleComponent]);

  const getRowClassName = useCallback(
    (row: GenericEntityType) => {
      return isAiGenerated(row) ? classes.aiSuggestedRow : undefined;
    },
    [isAiGenerated, classes.aiSuggestedRow],
  );

  const isMaxItemsReached =
    multiple && maxItems !== undefined && entities.length >= maxItems;

  const checkIfDisabled = useCallback(
    (row: GenericEntityType) => {
      const id = getRowId(row);
      const adapted = adapter(row);
      return !!(
        checkOptionDisabled?.(adapted) ||
        (isMaxItemsReached && !entityIdsSet.has(id))
      );
    },
    [isMaxItemsReached, entityIdsSet, getRowId, adapter, checkOptionDisabled],
  );

  return (
    <>
      {filtersSectionWithAI}
      {error && (
        <WbAlert message={errorToPreviewMessage(error)} variant="compact" />
      )}
      {isMaxItemsReached && (
        <WbAlert
          message={{
            type: 'warning',
            title: `Maximum selection reached`,
            content: `You can select at most ${maxItems} item${
              maxItems === 1 ? '' : 's'
            }. Deselect one to pick another.`,
          }}
          variant="compact"
        />
      )}
      <FormControl className={classes.formControl}>
        <WbTable<GenericEntityType>
          styles={{
            container: {
              flex: 1,
            },
          }}
          selection={multiple ? 'multiple' : 'single'}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          checkIfDisabled={checkIfDisabled}
          tooltipIfDisabled="This option is not available"
          components={{
            tableLoader: { loading },
            tableContent: {
              columns: tableColumns,
              rows: sortedData,
              getRowId: effectiveGetRowReactKey,
              rowDynamicClassName: getRowClassName,
              ...(treePropsConfig ? { treeProps: treePropsConfig } : {}),
            },
          }}
          pagination={{
            countlessOptions: pagination.countlessOptions,
            rowsPerPageOptions: [5, 10, 25],
            count: count,
            limit: pagination.limit ?? 0,
            currentPage: pagination.currentPage,
            onPageChange: handleChangePage,
            onRowsPerPageChange: handleChangeRowsPerPage,
          }}
        />
      </FormControl>
    </>
  );
};
